class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.shippingObserver = new MutationObserver(() => this.syncShippingCelebration());
    this.shippingObserver.observe(this, {childList: true, subtree: true, attributes: true, attributeFilter: ['class']});
    this.syncShippingCelebration();
  }

  disconnectedCallback() {
    this.shippingObserver?.disconnect();
    clearTimeout(this.autoCloseTimer);
    this.stopShippingCelebration();
  }

  syncShippingCelebration() {
    const progress = this.querySelector('[data-shipping-qualified]');
    const qualified = progress?.dataset.shippingQualified === 'true';
    if (!qualified) {
      this.shippingCelebrated = false;
      this.stopShippingCelebration();
      return;
    }
    if (!this.classList.contains('active') || this.shippingCelebrated) return;
    this.shippingCelebrated = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.celebrateFreeShipping(progress);
  }

  stopShippingCelebration() {
    cancelAnimationFrame(this.shippingFrame);
    clearTimeout(this.shippingPulseTimer);
    this.shippingCanvas?.remove();
    this.shippingCanvas = null;
    this.querySelector('.shipping-celebrating')?.classList.remove('shipping-celebrating');
  }

  celebrateFreeShipping(progress) {
    this.stopShippingCelebration();
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;
    progress.classList.add('shipping-celebrating');
    this.shippingPulseTimer = setTimeout(() => progress.classList.remove('shipping-celebrating'), 1700);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    const bounds = panel.getBoundingClientRect();
    const origin = progress.getBoundingClientRect();
    const width = bounds.width;
    const height = bounds.height;
    if (!width || !height) return;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.className = 'jaimin-shipping-confetti';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.width = width * scale;
    canvas.height = height * scale;
    canvas.style.cssText = `position:absolute;left:0;top:0;width:${width}px;height:${height}px;pointer-events:none;z-index:1000;`;
    context.scale(scale, scale);
    this.shippingCanvas = canvas;
    panel.appendChild(canvas);
    const colors = ['#ef2f35', '#ffca42', '#2caa74', '#8d65c5', '#ff8d51', '#64c7ec'];
    const launchY = Math.min(height - 40, Math.max(110, origin.top - bounds.top + origin.height / 2));
    const particles = Array.from({length: 110}, (_, index) => {
      const left = index % 2 === 0;
      return {x: left ? 10 : width - 10, y: launchY, vx: (left ? 1 : -1) * (1.5 + Math.random() * 5.5), vy: -4 - Math.random() * 7, rotation: Math.random() * Math.PI, spin: (Math.random() - .5) * .24, size: 4 + Math.random() * 5, color: colors[index % colors.length], delay: Math.random() * 150};
    });
    let started;
    let previous;
    const frame = now => {
      if (!this.classList.contains('active') || !this.isConnected) {
        this.stopShippingCelebration();
        return;
      }
      started ??= now;
      const elapsed = now - started;
      const step = Math.min((now - (previous ?? now)) / 16.667, 2);
      previous = now;
      context.clearRect(0, 0, width, height);
      for (const particle of particles) {
        if (elapsed < particle.delay) continue;
        particle.x += particle.vx * step;
        particle.y += particle.vy * step;
        particle.vy += .17 * step;
        particle.vx *= Math.pow(.992, step);
        particle.rotation += particle.spin * step;
        context.save();
        context.translate(particle.x, particle.y);
        context.rotate(particle.rotation);
        context.globalAlpha = Math.max(0, Math.min(1, (2900 - elapsed) / 600));
        context.fillStyle = particle.color;
        context.fillRect(-particle.size / 2, -particle.size / 3, particle.size, particle.size * .6);
        context.restore();
      }
      if (elapsed < 2900) this.shippingFrame = requestAnimationFrame(frame);
      else this.stopShippingCelebration();
    };
    this.shippingFrame = requestAnimationFrame(frame);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    clearTimeout(this.autoCloseTimer);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.syncShippingCelebration();
      this.autoCloseTimer = setTimeout(() => this.close(), 5000);
    });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    giftCart();
  }

  close() {
    clearTimeout(this.autoCloseTimer);
    this.stopShippingCelebration();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
     giftCart();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      giftCart();
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
     
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function giftCart() {
  const cartItems = document.querySelectorAll('tr.cart-item');
    cartItems.forEach(cartItem => {        
        const dataId = cartItem.getAttribute('data-id');
        const drawerFooter = document.querySelector(`#Gift-Wrapper[data-id="${dataId}"]`);  
      if(!drawerFooter) return;
      const drawerFooterID = drawerFooter.getAttribute('data-id');
        if(drawerFooterID == dataId) {            
            drawerFooter.classList.add('Gift-Wrapper__hide'); 
        }
    });
}

